import {
  IAssignmentHistory,
  IAssignmentView,
  ITask,
  ITaskDocumentView,
  ITasks,
  ITaskUpdateView,
} from './tasksType';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICompany,
  ICompanyServer,
  ICompanyTemplate,
  IProject,
} from '../companies/companiesTypes';
import * as taskManipulate from './taskManipulate';
import * as manApi from 'api/ManApi';
import { RootState, TThunkApi } from 'store/store';
import { ISearchParams, NumberOrNull, StringOrNull } from 'store/entity/types';
import { ITemplateReportMark } from '../templates/templatesSlice';
import cloneDeep from 'lodash/cloneDeep';

const initialState: ITasks = {
  data: {},
  dirty: [],
  isLoading: false,
  showDeleted: false,
};

// Reducers
function loadingTasksHandler(state: ITasks) {
  state.isLoading = true;
}

function setTasksDataHandler(
  state: ITasks,
  action: PayloadAction<{ tasks: Array<ITask> }>
) {
  action.payload.tasks.forEach((task) => {
    state.data[task.id] = taskManipulate.serverToStore(task);
  });
  state.isLoading = false;
}

function showDeletedActionHandler(
  state: ITasks,
  action: PayloadAction<{ show: boolean }>
) {
  state.showDeleted = action.payload.show;
}

function setSingleTaskDataHandler(
  state: ITasks,
  action: PayloadAction<{ task: ITask }>
) {
  state.data[action.payload.task.id] = taskManipulate.serverToStore(
    action.payload.task
  );
  // if (action.payload.task.parents)
  //   action.payload.task.parents.forEach((it) => {
  //     state.data[it.id] = taskManipulate.serverToStore(it);
  //   });
}

function mentionsResolvedHandler(
  state: ITasks,
  action: PayloadAction<{
    mentionId: number | undefined;
    taskId: number | undefined;
  }>
) {
  if (!action.payload.taskId) return;

  state.data[action.payload.taskId].mentions =
    state.data[action.payload.taskId!].mentions?.filter((mention) => {
      return (
        action.payload.mentionId && mention.id !== action.payload.mentionId
      );
    }) || [];
}

function deleteSingleTaskHandler(
  state: ITasks,
  action: PayloadAction<{
    taskUpdateView: ITaskUpdateView;
  }>
) {
  if (
    state.data[action.payload.taskUpdateView.task.id] &&
    action.payload.taskUpdateView.affected[
      action.payload.taskUpdateView.task.id
    ] !== 'parentDeleted'
  ) {
    delete state.data[action.payload.taskUpdateView.task.id];
  }
}

function updateTaskAffectionHandler(
  state: ITasks,
  action: PayloadAction<{
    taskUpdateView: ITaskUpdateView;
  }>
) {
  Object.values(state.data).forEach((task) => {
    switch (action.payload.taskUpdateView.affected[task.id]) {
      case 'drafted':
        state.data[task.id].draft = true;
        break;
      case 'undrafted':
        state.data[task.id].draft = false;
        break;
      case 'deleted':
        state.data[task.id].deleteState = 'deleted';
        break;
      case 'archived':
        state.data[task.id].archived = true;
        break;
      case 'unArchived':
        state.data[task.id].archived = false;
        break;
    }
  });
}

function setDirtyHandler(
  state: ITasks,
  action: PayloadAction<{
    taskId: number;
    isDirty: boolean;
  }>
) {
  if (action.payload.isDirty) {
    const dirty = state.dirty;
    if (!dirty?.includes(action.payload.taskId))
      dirty.push(action.payload.taskId);

    state.dirty = dirty;
  } else {
    state.dirty = state.dirty.filter((id) => id !== action.payload.taskId);
  }
}

function updateTaskAssignmentHandler(
  state: ITasks,
  action: PayloadAction<{
    assignment: IAssignmentView;
  }>
) {
  if (!action.payload.assignment.taskId) return;

  const taskId = action.payload.assignment.taskId;
  const task = cloneDeep(state.data[taskId]) as ITask;

  if (!task) return;

  if (action.payload.assignment.taskFieldValues) {
    task.fields = task.fields?.map((el) => {
      if (
        action.payload.assignment.taskFieldValues &&
        Object.keys(action.payload.assignment.taskFieldValues || {}).includes(
          el.name
        )
      ) {
        return {
          ...el,
          value: action.payload.assignment.taskFieldValues[el.name],
        };
      } else return el;
    });
  }

  if (action.payload.assignment?.taskView?.mainProgress) {
    state.data[taskId].mainProgress =
      action.payload.assignment?.taskView?.mainProgress;
  }

  state.data[taskId] = task;

  if (
    action.payload.assignment.type === 'does' &&
    action.payload.assignment.taskId
  ) {
    state.data[action.payload.assignment.taskId].assignee[
      action.payload.assignment.userId
    ] = action.payload.assignment;
  } else if (action.payload.assignment.type === 'verify') {
    state.data[action.payload.assignment.taskId].verifiers[
      action.payload.assignment.userId
    ] = action.payload.assignment;
  }
}
function task_setCompaniesDataHandler(
  state: ITasks,
  action: PayloadAction<{
    companies: ICompany[];
    thisUserId: number;
    noStub: boolean;
  }>
) {
  action.payload.companies.forEach((company) => {
    Object.values(company.templates).forEach((companyTemplate) => {
      if (companyTemplate.task) {
        const newTask = taskManipulate.serverToStore(companyTemplate.task);
        newTask.template = true;
        state.data[companyTemplate.taskId] = newTask;
      }
    });
  });
}

function companySetTemplateDataHandler(
  state: ITasks,
  action: PayloadAction<{ template: ICompanyTemplate }>
) {
  state.data[action.payload.template.taskId] = action.payload.template.task!;
}

function tasks_setCompanyTemplateDataHandler(
  state: ITasks,
  action: PayloadAction<{ template: ICompanyTemplate }>
) {
  if (!action.payload.template.task) return;
  state.data[action.payload.template.taskId] = action.payload.template.task;
}

function tasks_getCompanyHandler(
  state: ITasks,
  action: PayloadAction<{ company: ICompanyServer; thisUserId: number }>
) {
  if (!action.payload.company.stub) {
    action.payload.company.templates?.forEach((template) => {
      const newTemplate = taskManipulate.serverToStore(template.task);
      newTemplate.template = true;
      state.data[template.taskId] = newTemplate;
    });
  }
}
function tasks_setSingleProjectHandler(
  state: ITasks,
  action: PayloadAction<{
    project: IProject;
  }>
) {
  if (action.payload.project.stub === false) {
    action.payload.project.templates?.forEach((template) => {
      const newTemplate = taskManipulate.serverToStore(template.task);
      newTemplate.template = true;
      state.data[template.taskId] = newTemplate;
    });
  }
}

function setTaskAttachement(
  state: ITasks,
  action: PayloadAction<{
    taskId: number;
    attachments: ITaskDocumentView[];
  }>
) {
  if (action.payload.attachments) {
    state.data[action.payload.taskId].attachments = action.payload.attachments;
  }
}

// Thunks
export const getTaskFn = async (
  {
    taskId,
    includeAssignmentHistory,
    includeProgressHistory,
    force,
  }: {
    taskId: number;
    includeAssignmentHistory?: boolean;
    includeProgressHistory?: boolean;
    force?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const tasks = store.tasks.data[taskId];

  if (tasks && !force) {
    return Promise.resolve(null);
  }

  return manApi
    .getSingleTask(
      store,
      taskId,
      includeAssignmentHistory,
      includeProgressHistory
    )
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({ data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getTask = createAsyncThunk('tasks/getTask', getTaskFn);

export const getSingleTaskFn = async (
  {
    taskId,
    includeAssignmentHistory,
    includeProgressHistory,
    includeParentTasks,
  }: {
    taskId: number;
    includeAssignmentHistory?: boolean;
    includeProgressHistory?: boolean;
    includeParentTasks?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .getSingleTask(
      store,
      taskId,
      includeAssignmentHistory,
      includeProgressHistory,
      includeParentTasks
    )
    .then(({ data: task }) => {
      ThunkApi.dispatch(setSingleTaskData({ task }));
      return Promise.resolve({ task });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getSingleTask = createAsyncThunk('tasks/getSingleTask', getSingleTaskFn);

export const getChildrenTasksFn = async (
  {
    parentId,
    projectId,
    companyId,
  }: { parentId: number; projectId: number; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .searchTasks(store, {
      from: 0,
      count: 100,
      order: 'creationDate_desc',
      parentId,
      projectId,
      companyId,
    })
    .then(({ data }) => {
      return Promise.resolve({ data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getChildrenTasks = createAsyncThunk(
  'tasks/getChildrenTasks',
  getChildrenTasksFn
);

export const deleteSingleTaskFn = async (
  { taskId }: { taskId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .deleteSingleTask(store, taskId)
    .then(({ data }) => {
      ThunkApi.dispatch(
        deleteSingleTaskReducer({
          taskUpdateView: data,
        })
      );
      return Promise.resolve({
        taskUpdateView: data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const deleteSingleTask = createAsyncThunk(
  'tasks/deleteSingleTask',
  deleteSingleTaskFn
);

export const restoreTaskFn = async (
  { taskId }: { taskId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .restoreTask(store, taskId)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({ task: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const restoreTask = createAsyncThunk('tasks/restoreTask', restoreTaskFn);

export const searchTasksFn = async (
  { params }: { params: ISearchParams },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const { from, count, parentId, ...rest } = params;
  const safeParentId = parentId ? parentId : parentId === null ? '' : -1;
  const showDeleted = store.tasks.showDeleted;
  const searchParams: ISearchParams = {
    from: from || 0,
    count: count || 20,
    parentId: safeParentId as any,
    ...rest,
    deleteState: showDeleted ? 'all' : 'notDeleted',
  };

  const searchTasksHandler = manApi.searchTasks(store, searchParams);

  if (params?.printable) {
    return searchTasksHandler
      .then(({ data: tasks }) => {
        return Promise.resolve({ tasks: tasks });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  } else {
    return searchTasksHandler
      .then(({ data: tasks }) => {
        ThunkApi.dispatch(setTasksData({ tasks }));
        return Promise.resolve({ tasks });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
};

const searchTasks = createAsyncThunk('tasks/searchTasks', searchTasksFn);
export const getAllTasksFn = async (
  params: {
    taskIdList: Array<number | string>;
    includeProgressHistory: boolean;
    includeAssignmentHistory: boolean;
    includeParentTasks: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const {
    taskIdList,
    includeProgressHistory,
    includeAssignmentHistory,
    includeParentTasks,
  } = params;

  const getAll = manApi.getAllTasks(
    store,
    taskIdList,
    includeProgressHistory,
    includeAssignmentHistory,
    includeParentTasks
  );

  return getAll
    .then(({ data: tasks }) => {
      ThunkApi.dispatch(setTasksData({ tasks }));
      return Promise.resolve({ tasks });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getAllTasks = createAsyncThunk('tasks/getAllTasks', getAllTasksFn);

export const createTaskFn = async (
  {
    task,
    dontSendMessages = false,
  }: { task: ITask; dontSendMessages?: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  task.template = false;
  return manApi
    .createTask(store, task, dontSendMessages)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({ createdTask: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const createTask = createAsyncThunk('tasks/createTask', createTaskFn);

export const markTaskAssignmentHistoryFn = async (
  {
    assignmentId,
    assignmentHistory,
  }: { assignmentId: number; assignmentHistory: IAssignmentHistory[] },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const thisUserId = Number(store.thisUser.userId);
  return manApi
    .markTaskAssignmentHistoryHandler(
      store,
      assignmentId,
      assignmentHistory.filter((history) => history.userId === thisUserId)
    )
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({ createdTask: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const markTaskAssignmentHistory = createAsyncThunk(
  'tasks/markTaskAssignmentHistory',
  markTaskAssignmentHistoryFn
);

export const updateTaskFn = async (
  {
    task,
    dontSendMessages = false,
  }: { task: ITask; dontSendMessages?: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  // @TODO check why we delete verifier ???????
  // delete task?.['verifier'];

  return manApi
    .updateTask(store, task, dontSendMessages)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({
        updatedTask: data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const updateTask = createAsyncThunk('tasks/updateTask', updateTaskFn);

export const addTaskAttachementFn = async (
  {
    taskId,
    file,
    onProgressCallback,
  }: {
    taskId: number;
    file: any;
    onProgressCallback: (progressEvent: any) => void;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .handleAddAttachementsToTask(store, taskId, file, onProgressCallback)
    .then(({ data }) => {
      return Promise.resolve({ taskId: taskId, attachments: data.attachments });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const addTaskAttachement = createAsyncThunk(
  '/addAttachment',
  addTaskAttachementFn
);

export const moveToProjectFn = async (
  { taskId, projectId }: { taskId: number; projectId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .moveToProject(store, taskId, projectId)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data }));
      return Promise.resolve({
        updatedTask: data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const moveToProject = createAsyncThunk('tasks/moveToProject', moveToProjectFn);

export const resolveMentionFn = async (
  {
    mentionId,
    taskId,
  }: { mentionId: number | undefined; taskId?: number | undefined },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .resolveMention(store, mentionId, !mentionId ? taskId : undefined)
    .then(() => {
      ThunkApi.dispatch(mentionsResolved({ mentionId, taskId }));
      return Promise.resolve({ mentionId, taskId });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const resolveMention = createAsyncThunk(
  'tasks/resolveMention',
  resolveMentionFn
);

export const undraftTaskFn = async (
  { taskId, includeSubTasks }: { taskId: number; includeSubTasks: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .undraftTask(store, taskId, includeSubTasks)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data.task }));
      ThunkApi.dispatch(updateTaskAffection({ taskUpdateView: data }));
      return Promise.resolve({ taskUpdateView: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const undraftTask = createAsyncThunk('tasks/undraftTask', undraftTaskFn);

export const draftTaskFn = async (
  { taskId }: { taskId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .draftTask(store, taskId)
    .then(({ data }) => {
      ThunkApi.dispatch(setSingleTaskData({ task: data.task }));
      ThunkApi.dispatch(updateTaskAffection({ taskUpdateView: data }));
      return Promise.resolve({ taskUpdateView: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const draftTask = createAsyncThunk('tasks/draftTask', draftTaskFn);

export const duplicateTaskFn = async (
  { taskIds }: { taskIds: Array<number> },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .cloneTask(store, taskIds)
    .then(({ data }) => {
      ThunkApi.dispatch(setTasksData({ tasks: data }));
      return Promise.resolve({ createdTask: data[0] });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const duplicateTask = createAsyncThunk('tasks/duplicateTask', duplicateTaskFn);

export const sendMessageToAssigneeFn = async (
  { taskId, message }: { taskId: number; message: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .sendMessageToAssignee(store, taskId, message)
    .then(({ data }) => {
      return Promise.resolve(data);
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const sendMessageToAssignee = createAsyncThunk(
  'tasks/sendMessageToAssignee',
  sendMessageToAssigneeFn
);

export const markTaskAssignmentFn = async (
  { assignment }: { assignment: IAssignmentView },
  ThunkApi: TThunkApi
) => {
  if (!assignment.taskId) return;

  const store = ThunkApi.getState() as RootState;
  const fieldValues = assignment.taskFieldValues || {};
  let oldAssignment: IAssignmentView;
  if (assignment.type === 'does') {
    oldAssignment =
      store.tasks.data[assignment.taskId!].assignee[assignment.userId];
  } else if (assignment.type === 'verify') {
    oldAssignment =
      store.tasks.data[assignment.taskId!].verifiers[assignment.userId]!;
  }

  return manApi
    .taskMark(store, assignment)
    .then(({ data: assignment }) => {
      const updatedAssignment: IAssignmentView = cloneDeep(assignment);
      updatedAssignment.taskFieldValues = fieldValues;
      ThunkApi.dispatch(
        updateTaskAssignment({ assignment: updatedAssignment })
      );

      if (updatedAssignment?.taskView)
        ThunkApi.dispatch(
          setSingleTaskData({ task: updatedAssignment.taskView })
        );

      return Promise.resolve({ assignment: updatedAssignment });
    })
    .catch((err) => {
      ThunkApi.dispatch(
        updateTaskAssignment({
          assignment: oldAssignment,
        })
      );
      return ThunkApi.rejectWithValue({
        assignment: oldAssignment,
        err,
      });
    });
};
const markTaskAssignment = createAsyncThunk(
  'tasks/markTaskAssignment',
  markTaskAssignmentFn
);

export const getTaskReportFn = async (
  {
    companyId,
    templateReport,
    startDate,
    toDate,
    projectId = null,
    parentId = null,
    templateId,
    assigneeId = null,
    splitMonths = 1,
    splitField,
    fieldValues,
  }: {
    companyId: number;
    templateReport: ITemplateReportMark;
    startDate: number;
    toDate: number;
    projectId: NumberOrNull;
    parentId: NumberOrNull;
    templateId: number;
    assigneeId: NumberOrNull;
    splitMonths: number;
    splitField: StringOrNull;
    fieldValues: { [key: string]: string };
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .taskGetReport(
      store,
      templateReport,
      startDate,
      toDate,
      projectId,
      parentId,
      templateId,
      companyId,
      assigneeId,
      splitMonths,
      splitField,
      fieldValues
    )
    .then(({ data }) => {
      return Promise.resolve({ data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getTaskReport = createAsyncThunk('tasks/getTaskReport', getTaskReportFn);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState: initialState,
  reducers: {
    tasks_setCompanyTemplateData: tasks_setCompanyTemplateDataHandler,
    task_setCompaniesData: task_setCompaniesDataHandler,
    tasks_getCompany: tasks_getCompanyHandler,
    tasks_setSingleProject: tasks_setSingleProjectHandler,
    loadingTasks: loadingTasksHandler,
    setTasksData: setTasksDataHandler,
    showDeletedAction: showDeletedActionHandler,
    setSingleTaskData: setSingleTaskDataHandler,
    mentionsResolved: mentionsResolvedHandler,
    deleteSingleTaskReducer: deleteSingleTaskHandler,
    updateTaskAffection: updateTaskAffectionHandler,
    setDirty: setDirtyHandler,
    updateTaskAssignment: updateTaskAssignmentHandler,
    companySetTemplateData: companySetTemplateDataHandler,
  },
  extraReducers: (builder) => {
    builder.addCase(addTaskAttachement.fulfilled, (state, action) =>
      setTaskAttachement(state, action)
    );
  },
});

export const {
  tasks_setCompanyTemplateData,
  task_setCompaniesData,
  tasks_getCompany,
  tasks_setSingleProject,
  loadingTasks,
  setTasksData,
  showDeletedAction,
  setSingleTaskData,
  mentionsResolved,
  deleteSingleTaskReducer,
  updateTaskAffection,
  setDirty,
  updateTaskAssignment,
  companySetTemplateData,
} = tasksSlice.actions;

export {
  getSingleTask,
  getChildrenTasks,
  deleteSingleTask,
  restoreTask,
  searchTasks,
  getAllTasks,
  createTask,
  updateTask,
  moveToProject,
  resolveMention,
  undraftTask,
  draftTask,
  duplicateTask,
  sendMessageToAssignee,
  markTaskAssignment,
  getTaskReport,
  getTask,
  addTaskAttachement,
  markTaskAssignmentHistory,
};

export default tasksSlice.reducer;
